/**
 * The TOTAL entity-ref namer (R17-prep) — what the engine calls any resolved
 * reference, item or not.
 *
 * @remarks
 * Every consumer that names clarification candidates had re-derived this
 * partially: the browser app and the CLI both assumed every candidate ref is
 * an item and called `requireItem` — which THROWS the moment a which-entity
 * ask includes the room (D41: always referable) or the hands (D63: always
 * referable). Only the demo terminal filtered them, privately. One total
 * function ends the class: item refs name their item, the room ref names
 * {@link ROOM_ITSELF_NAME}, the body ref names {@link BODY_NAME} — the same
 * framing names the refusal slots already speak, so a chip and a refusal can
 * never disagree about what a thing is called.
 *
 * @packageDocumentation
 */

import type { EntityRef } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor, requireItem } from '../bundle/bundle-lookups.js';
import { actorIdForEntityRef, BODY_NAME, isBodyRef, itemIdForEntityRef, ROOM_ITSELF_NAME, roomIdForEntityRef, topicWordForEntityRef } from './entity-ref.js';

/**
 * Name any resolved entity reference, totally.
 *
 * @param bundle - The loaded bundle (item names)
 * @param entityRef - Any reference the parser resolved from the catalog
 * @returns The display name — an item's or an actor's name, a topic's word, or the engine's own framing for the room / the hands
 */
export const describeEntityRef = (bundle: EngineGameBundle, entityRef: EntityRef): string => {
	if (isBodyRef(entityRef)) {
		return BODY_NAME;
	}

	if (roomIdForEntityRef(entityRef) !== undefined) {
		return ROOM_ITSELF_NAME;
	}

	// D231: a topic word is called by the word — it has no other name.
	const topicWord = topicWordForEntityRef(entityRef);

	if (topicWord !== undefined) {
		return topicWord;
	}

	// D232: an actor by its name — "the woman in the sump", as the pack wrote it.
	const actorId = actorIdForEntityRef(entityRef);

	if (actorId !== undefined) {
		return requireActor(bundle, actorId).name;
	}

	return requireItem(bundle, itemIdForEntityRef(entityRef)).name;
};
